/**
 * @file mockData.ts
 * @description Realistic synthetic hospital data for local development and
 *              UI demonstration. Pre-seeds all three views with data that
 *              exercises all 4 reconciliation rules.
 *
 * Toggle between mock and live data in App.tsx via the MOCK_MODE flag.
 */

import {
  UnifiedRecord,
  AuditLogEntry,
  SystemMetrics,
} from "../types";

// ─────────────────────────────────────────────────────────────────────────────
// Helper
// ─────────────────────────────────────────────────────────────────────────────
const now = new Date();
const ts = (daysAgo: number, hours = 0) =>
  new Date(now.getTime() - daysAgo * 86400000 - hours * 3600000).toISOString();

// ─────────────────────────────────────────────────────────────────────────────
// MOCK UNIFIED RECORDS
// Each record exercises one or more reconciliation rules.
// ─────────────────────────────────────────────────────────────────────────────
export const MOCK_UNIFIED_RECORDS: UnifiedRecord[] = [
  // ── RULE 1: Status Mismatch — HIS=Discharged, Bed=Occupied ─────────────────
  {
    patient_id: "P-10042",
    patient_name: "Margaret Chen",
    resolved_bed: "B-07",
    ward: "Cardiology",
    resolved_bed_status: "Bed Requires Cleaning/Check",
    his_clinical_status: "Discharged",
    admission_datetime: ts(3),
    discharge_datetime: ts(0, 3),
    diagnosis_code: "I21.3",
    lab_results: [
      {
        order_id: "LAB-001",
        test_name: "Troponin",
        order_datetime: ts(3),
        result_datetime: ts(2, 12),
        tat_minutes: 47,
        result_status: "Completed",
        priority: "STAT",
        post_discharge_anomaly: false,
      },
    ],
    avg_lab_tat_minutes: 47,
    conflict_flags: ["Status Mismatch"],
    has_conflict: true,
    is_orphaned_lab_record: false,
    last_reconciled_at: ts(0),
    sources_present: ["HIS", "Lab", "BedSheet"],
    doctor_visits: 3,
    doctor_charge: 1500,
    lab_charge: 500,
    room_charge: 4500,
    surgery_charge: 0,
    total_charge: 6500,
  },

  // ── RULE 2: Location Desync — HIS=A-12, BedSheet=C-04 ─────────────────────
  {
    patient_id: "P-10055",
    patient_name: "James Thornton",
    resolved_bed: "C-04", // Bed Sheet wins
    ward: "Neurology",
    resolved_bed_status: "Occupied",
    his_clinical_status: "Admitted",
    admission_datetime: ts(1),
    discharge_datetime: "",
    diagnosis_code: "G35",
    lab_results: [
      {
        order_id: "LAB-002",
        test_name: "MRI Brain w/ Contrast",
        order_datetime: ts(1),
        result_datetime: ts(0, 8),
        tat_minutes: 210,
        result_status: "Completed",
        priority: "Routine",
        post_discharge_anomaly: false,
      },
      {
        order_id: "LAB-003",
        test_name: "CSF Analysis",
        order_datetime: ts(0, 4),
        result_datetime: "",
        tat_minutes: 0,
        result_status: "Pending",
        priority: "STAT",
        post_discharge_anomaly: false,
      },
    ],
    avg_lab_tat_minutes: 210,
    conflict_flags: ["Location Desync"],
    has_conflict: true,
    is_orphaned_lab_record: false,
    last_reconciled_at: ts(0),
    sources_present: ["HIS", "Lab", "BedSheet"],
    doctor_visits: 1,
    doctor_charge: 500,
    lab_charge: 2500,
    room_charge: 1500,
    surgery_charge: 0,
    total_charge: 4500,
  },

  // ── RULE 3: Orphaned Lab Record — no HIS match ─────────────────────────────
  {
    patient_id: "ORPHAN-P-99901",
    patient_name: "Unknown (Lab Patient P-99901)",
    resolved_bed: "",
    ward: "",
    resolved_bed_status: "Orphaned",
    his_clinical_status: "",
    admission_datetime: "",
    discharge_datetime: "",
    diagnosis_code: "",
    lab_results: [
      {
        order_id: "LAB-004",
        test_name: "CBC",
        order_datetime: ts(0, 6),
        result_datetime: ts(0, 5),
        tat_minutes: 62,
        result_status: "Completed",
        priority: "Routine",
        post_discharge_anomaly: false,
      },
      {
        order_id: "LAB-005",
        test_name: "BMP",
        order_datetime: ts(0, 6),
        result_datetime: "",
        tat_minutes: 0,
        result_status: "Pending",
        priority: "Routine",
        post_discharge_anomaly: false,
      },
    ],
    avg_lab_tat_minutes: 62,
    conflict_flags: ["Orphaned Lab Record"],
    has_conflict: true,
    is_orphaned_lab_record: true,
    last_reconciled_at: ts(0),
    sources_present: ["Lab"],
    doctor_visits: 0,
    doctor_charge: 0,
    lab_charge: 1000,
    room_charge: 0,
    surgery_charge: 0,
    total_charge: 1000,
  },

  // ── RULE 4: Post-Discharge Anomaly ─────────────────────────────────────────
  {
    patient_id: "P-10061",
    patient_name: "Aisha Patel",
    resolved_bed: "D-03",
    ward: "General Surgery",
    resolved_bed_status: "Available",
    his_clinical_status: "Discharged",
    admission_datetime: ts(5),
    discharge_datetime: ts(0, 12),
    diagnosis_code: "K35.89",
    lab_results: [
      {
        order_id: "LAB-006",
        test_name: "Culture & Sensitivity",
        order_datetime: ts(1),
        result_datetime: ts(0, 6), // 6h after discharge
        tat_minutes: 420,
        result_status: "Completed",
        priority: "Routine",
        post_discharge_anomaly: true, // RULE 4 TRIGGERED
      },
    ],
    avg_lab_tat_minutes: 420,
    conflict_flags: ["Post-Discharge Anomaly"],
    has_conflict: true,
    is_orphaned_lab_record: false,
    last_reconciled_at: ts(0),
    sources_present: ["HIS", "Lab", "BedSheet"],
    doctor_visits: 5,
    doctor_charge: 2500,
    lab_charge: 1200,
    room_charge: 7500,
    surgery_charge: 35000,
    total_charge: 46200,
  },

  // ── CLEAN: No conflicts ────────────────────────────────────────────────────
  {
    patient_id: "P-10071",
    patient_name: "Robert Okonkwo",
    resolved_bed: "A-01",
    ward: "ICU",
    resolved_bed_status: "Occupied",
    his_clinical_status: "Admitted",
    admission_datetime: ts(2),
    discharge_datetime: "",
    diagnosis_code: "J18.9",
    lab_results: [
      {
        order_id: "LAB-007",
        test_name: "Procalcitonin",
        order_datetime: ts(2),
        result_datetime: ts(1, 20),
        tat_minutes: 28,
        result_status: "Completed",
        priority: "STAT",
        post_discharge_anomaly: false,
      },
      {
        order_id: "LAB-008",
        test_name: "Blood Culture x2",
        order_datetime: ts(1),
        result_datetime: ts(0, 2),
        tat_minutes: 78,
        result_status: "Completed",
        priority: "STAT",
        post_discharge_anomaly: false,
      },
    ],
    avg_lab_tat_minutes: 53,
    conflict_flags: ["Clean"],
    has_conflict: false,
    is_orphaned_lab_record: false,
    last_reconciled_at: ts(0),
    sources_present: ["HIS", "Lab", "BedSheet"],
    doctor_visits: 2,
    doctor_charge: 1000,
    lab_charge: 3400,
    room_charge: 12000,
    surgery_charge: 0,
    total_charge: 16400,
  },
  {
    patient_id: "P-10082",
    patient_name: "Elena Vasquez",
    resolved_bed: "A-02",
    ward: "ICU",
    resolved_bed_status: "Occupied",
    his_clinical_status: "Admitted",
    admission_datetime: ts(1),
    discharge_datetime: "",
    diagnosis_code: "N17.9",
    lab_results: [
      {
        order_id: "LAB-009",
        test_name: "Creatinine",
        order_datetime: ts(1),
        result_datetime: ts(0, 18),
        tat_minutes: 35,
        result_status: "Completed",
        priority: "Routine",
        post_discharge_anomaly: false,
      },
    ],
    avg_lab_tat_minutes: 35,
    conflict_flags: ["Clean"],
    has_conflict: false,
    is_orphaned_lab_record: false,
    last_reconciled_at: ts(0),
    sources_present: ["HIS", "Lab", "BedSheet"],
    doctor_visits: 1,
    doctor_charge: 500,
    lab_charge: 500,
    room_charge: 6000,
    surgery_charge: 0,
    total_charge: 7000,
  },
  // Available beds (no patient)
  {
    patient_id: "BED-ONLY-A-03",
    patient_name: "",
    resolved_bed: "A-03",
    ward: "ICU",
    resolved_bed_status: "Available",
    his_clinical_status: "",
    admission_datetime: "",
    discharge_datetime: "",
    diagnosis_code: "",
    lab_results: [],
    avg_lab_tat_minutes: 0,
    conflict_flags: ["Clean"],
    has_conflict: false,
    is_orphaned_lab_record: false,
    last_reconciled_at: ts(0),
    sources_present: ["BedSheet"],
  },
  {
    patient_id: "BED-ONLY-B-01",
    patient_name: "",
    resolved_bed: "B-01",
    ward: "Cardiology",
    resolved_bed_status: "Pending Clean",
    his_clinical_status: "",
    admission_datetime: "",
    discharge_datetime: "",
    diagnosis_code: "",
    lab_results: [],
    avg_lab_tat_minutes: 0,
    conflict_flags: ["Clean"],
    has_conflict: false,
    is_orphaned_lab_record: false,
    last_reconciled_at: ts(0),
    sources_present: ["BedSheet"],
  },
  {
    patient_id: "BED-ONLY-C-01",
    patient_name: "",
    resolved_bed: "C-01",
    ward: "Neurology",
    resolved_bed_status: "Maintenance",
    his_clinical_status: "",
    admission_datetime: "",
    discharge_datetime: "",
    diagnosis_code: "",
    lab_results: [],
    avg_lab_tat_minutes: 0,
    conflict_flags: ["Clean"],
    has_conflict: false,
    is_orphaned_lab_record: false,
    last_reconciled_at: ts(0),
    sources_present: ["BedSheet"],
  },
  {
    patient_id: "P-10091",
    patient_name: "David Kim",
    resolved_bed: "D-01",
    ward: "General Surgery",
    resolved_bed_status: "Occupied",
    his_clinical_status: "Admitted",
    admission_datetime: ts(0, 8),
    discharge_datetime: "",
    diagnosis_code: "K80.20",
    lab_results: [],
    avg_lab_tat_minutes: 0,
    conflict_flags: ["Clean"],
    has_conflict: false,
    is_orphaned_lab_record: false,
    last_reconciled_at: ts(0),
    sources_present: ["HIS", "BedSheet"],
    doctor_visits: 1,
    doctor_charge: 500,
    lab_charge: 0,
    room_charge: 1500,
    surgery_charge: 45000,
    total_charge: 47000,
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// MOCK AUDIT LOG
// ─────────────────────────────────────────────────────────────────────────────
export const MOCK_AUDIT_LOG: AuditLogEntry[] = [
  {
    id: "audit-001",
    timestamp: ts(0, 0.5),
    patient_id: "P-10042",
    patient_name: "Margaret Chen",
    conflict_type: "Status Mismatch",
    conflict_detail:
      "HIS clinical status is 'Discharged' but Bed Sheet records bed 'B-07' as 'Occupied'. Patient may have left but bed has not yet been cleared.",
    sources_involved: ["HIS", "BedSheet"],
    resolution_applied:
      "Resolved bed status to 'Bed Requires Cleaning/Check'. HIS clinical status 'Discharged' retained as authoritative.",
    reconciliation_run_id: "RUN-MOCK-2024-08-01",
  },
  {
    id: "audit-002",
    timestamp: ts(0, 0.5),
    patient_id: "P-10055",
    patient_name: "James Thornton",
    conflict_type: "Location Desync",
    conflict_detail:
      "HIS records patient in bed 'A-12' but Bed Sheet records the same patient in bed 'C-04'. This may indicate a manual bed transfer not yet reflected in HIS.",
    sources_involved: ["HIS", "BedSheet"],
    resolution_applied:
      "Resolved bed assignment to 'C-04' (Bed Sheet authority for physical location). HIS bed 'A-12' preserved in raw data. Investigate whether a bed transfer order exists in HIS.",
    reconciliation_run_id: "RUN-MOCK-2024-08-01",
  },
  {
    id: "audit-003",
    timestamp: ts(0, 0.5),
    patient_id: "ORPHAN-P-99901",
    patient_name: "Unknown (Lab Patient P-99901)",
    conflict_type: "Orphaned Lab Record",
    conflict_detail:
      "Lab data contains 2 order(s) for patient_id 'P-99901', but this patient_id does not exist in the HIS export. Orders: LAB-004, LAB-005.",
    sources_involved: ["Lab"],
    resolution_applied:
      "Created a temporary unified profile marked as 'Orphaned Lab Record'. All lab results preserved. Action Required: Verify patient_id in Lab system and reconcile with HIS manually.",
    reconciliation_run_id: "RUN-MOCK-2024-08-01",
  },
  {
    id: "audit-004",
    timestamp: ts(0, 0.5),
    patient_id: "P-10061",
    patient_name: "Aisha Patel",
    conflict_type: "Post-Discharge Anomaly",
    conflict_detail:
      "Lab order 'LAB-006' (Culture & Sensitivity) was resulted 6 hours AFTER the HIS discharge timestamp. This order may have been drawn pre-discharge but processed after the patient left.",
    sources_involved: ["HIS", "Lab"],
    resolution_applied:
      "Lab result retained in unified record. Flagged as 'Post-Discharge Anomaly' for clinical and lab review. TAT calculation includes this result.",
    reconciliation_run_id: "RUN-MOCK-2024-08-01",
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// MOCK SYSTEM METRICS
// ─────────────────────────────────────────────────────────────────────────────
export const MOCK_SYSTEM_METRICS: SystemMetrics = {
  computed_at: ts(0),
  reconciliation_run_id: "RUN-MOCK-2024-08-01",
  total_beds: 32,
  occupied_beds: 18,
  available_beds: 10,
  pending_clean_beds: 3,
  flagged_beds: 4,
  occupancy_rate_pct: 56,
  total_lab_orders: 9,
  completed_lab_orders: 6,
  pending_lab_orders: 2,
  avg_lab_tat_minutes: 113,
  p90_lab_tat_minutes: 345,
  stat_orders: 4,
  total_conflicts: 4,
  status_mismatches: 1,
  location_desyncs: 1,
  orphaned_lab_records: 1,
  post_discharge_anomalies: 1,
  his_records_ingested: 8,
  lab_records_ingested: 9,
  bed_records_ingested: 12,
};

// ─────────────────────────────────────────────────────────────────────────────
// LAB TAT TREND DATA (for Recharts)
// ─────────────────────────────────────────────────────────────────────────────
export const MOCK_TAT_TREND = [
  { time: "06:00", avg_tat: 45, p90_tat: 88 },
  { time: "08:00", avg_tat: 62, p90_tat: 120 },
  { time: "10:00", avg_tat: 113, p90_tat: 210 },
  { time: "12:00", avg_tat: 98, p90_tat: 190 },
  { time: "14:00", avg_tat: 87, p90_tat: 175 },
  { time: "16:00", avg_tat: 143, p90_tat: 345 },
  { time: "18:00", avg_tat: 125, p90_tat: 280 },
  { time: "20:00", avg_tat: 95, p90_tat: 220 },
  { time: "22:00", avg_tat: 78, p90_tat: 155 },
];
